import React, { useEffect } from "react";
import clsx from "clsx";
import { PanelLeftClose, SquarePen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogoRefabric, LogoRefabricMark } from "icons";
import { useMenuItems } from "./useMenuItems";
import NavTree from "./NavTree";
import { PATHS } from "router/config/paths";
import { confirmNavigation } from "utils/unsavedChangesGuard";
import { useAppDispatch, useAppSelector } from "store";
import { setSidebarCollapsed } from "store/commonSlice";

const SideMenu = ({ defaultCollapsed }: { defaultCollapsed?: boolean }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // Collapsed state is global (Redux) so the toggle can live in the page header (PageTitleBar).
  const collapsed = useAppSelector((s) => s.common.isSidebarCollapsed);
  const { sections, isExactActive, isBranchActive, handleItemClick, isAuthorized, newChat } = useMenuItems();

  // Some routes force the rail collapsed via layoutProps.sideMenuCollapsed (detail/studio pages).
  // Only ever force-COLLAPSE here — never force-expand — so the user's manual collapse survives
  // navigation to normal pages. (A page whose shell remounts across a redirect — e.g. the
  // retired /brand-dnas prefix → /moodboards/my — must not spring the rail back open.)
  useEffect(() => {
    if (defaultCollapsed) dispatch(setSidebarCollapsed(true));
  }, [defaultCollapsed, dispatch]);

  useEffect(() => {
    document.documentElement.style.setProperty("--sidebarWidth", collapsed ? "60px" : "236px");
  }, [collapsed]);

  return (
    // No edge of its own: the page beside it is a box (`Layout`), and the menu is the ground that box sits on.
    <div className={clsx("flex flex-col bg-body-100 transition-all duration-200", collapsed ? "w-[60px]" : "w-[236px]")}>
      <div className="flex flex-col h-full">
        {/* Logo — full-bleed 48px header, no bottom divider. The 16px inset lines the full logo's left
            edge up with the nav item icons below (nav container px-2 + item px-2). The collapse toggle
            sits beside it (the chat agent's sidebar); collapsed, the icon mark re-expands the rail. */}
        <div className={clsx("flex items-center h-12 flex-shrink-0", collapsed ? "justify-center" : "px-4")}>
          {collapsed ? (
            <button
              type="button"
              className="flex-center cursor-pointer text-trueBlack dark:text-white"
              onClick={() => dispatch(setSidebarCollapsed(false))}
              title={t("common:sidebar.expand_menu")}
              aria-label={t("common:sidebar.expand_menu")}
            >
              <LogoRefabricMark className="h-6 w-6" />
            </button>
          ) : (
            <>
              <LogoRefabric
                className="text-trueBlack dark:text-white h-5 cursor-pointer"
                onClick={async () => {
                  if (await confirmNavigation()) navigate(PATHS.HOME);
                }}
              />
              <button
                type="button"
                data-testid="sidebar-collapse"
                onClick={() => dispatch(setSidebarCollapsed(true))}
                title={t("common:sidebar.collapse_menu")}
                aria-label={t("common:sidebar.collapse_menu")}
                className="-mr-2.5 ml-auto flex h-8 w-8 items-center justify-center rounded-lg text-grey transition-colors hover:bg-trueBlack/[0.04] hover:text-trueBlack dark:hover:bg-trueWhite/5 dark:hover:text-white"
              >
                <PanelLeftClose size={18} />
              </button>
            </>
          )}
        </div>

        {/* New Chat: the design agent, fresh — the rail's first action, above every place. */}
        {newChat && (
          <div className={clsx("shrink-0 px-2 pt-0.5", collapsed && "flex justify-center")}>
            <button
              type="button"
              data-testid="nav-new-chat"
              onClick={() => void newChat()}
              title={t("common:nav.new_chat")}
              aria-label={t("common:nav.new_chat")}
              className={clsx(
                "flex h-8 items-center rounded-lg border border-black-300 text-[14px] text-trueBlack transition-colors hover:bg-trueBlack/[0.04] active:scale-[0.98] dark:text-white dark:hover:bg-trueWhite/5",
                collapsed ? "w-8 justify-center" : "w-full gap-2.5 px-2"
              )}
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                <SquarePen size={18} />
              </span>
              {!collapsed && <span className="truncate">{t("common:nav.new_chat")}</span>}
            </button>
          </div>
        )}

        {/* Navigation Sections */}
        <div className="flex flex-col flex-1 min-h-0 overflow-y-auto px-2 pb-3 pt-3">
          <NavTree sections={sections} isExactActive={isExactActive} isBranchActive={isBranchActive} onItemClick={handleItemClick} isAuthorized={isAuthorized} collapsed={collapsed} flyouts />
        </div>
      </div>
    </div>
  );
};

export default SideMenu;
